import { Button, Drawer, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { saveAvailability, type Availability, type WeeklyWindow } from "../../api/pt";
import { useServices } from "../../app/services";
import k from "./classes.module.css";
import { Field, FormError, messageOf, styles as c } from "./common";
import { WEEKDAY_NAMES } from "./time";

/** Weekly windows for one trainer at one branch. Date exceptions are kept as they are (this form does not edit them). */
export function AvailabilityDrawer({ open, onClose, trainerId, trainerName, branchId, current }: { open: boolean; onClose: () => void; trainerId: string; trainerName: string; branchId: string; current: Availability | null }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [rows, setRows] = useState<WeeklyWindow[]>(current?.weekly ?? []);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => saveAvailability(client, trainerId, { branchId, weekly: rows, exceptions: current?.exceptions ?? [] }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["pt"] }); toast.show("Availability saved"); onClose(); },
    onError: (e) => setError(messageOf(e)),
  });
  const submit = () => {
    if (rows.some((r) => !r.start || !r.end || r.end <= r.start)) return setError("Each window needs an end time after its start.");
    setError(null);
    save.mutate();
  };
  const patch = (i: number, p: Partial<WeeklyWindow>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()} title={`${trainerName}: availability`} description="When this trainer takes PT sessions each week."
      footer={<div className={c.row}><Button variant="primary" busy={save.isPending} onClick={submit}>Save availability</Button><Button variant="plain" onClick={onClose}>Cancel</Button></div>}>
      <div className={`${c.stack} ${c.drawerPad}`}>
        {rows.length === 0 ? <p className={c.note}>No windows yet: no one can book this trainer.</p> : null}
        {rows.map((r, i) => (
          <div key={i} className={`${k.slotRow} ${k.slotRow4}`}>
            <Field label={`Day ${i + 1}`} htmlFor={`av-day-${i}`}><select id={`av-day-${i}`} className={c.input} value={r.weekday} onChange={(e) => patch(i, { weekday: Number(e.target.value) })}>{WEEKDAY_NAMES.map((n, w) => <option key={n} value={w}>{n}</option>)}</select></Field>
            <Field label={`From ${i + 1}`} htmlFor={`av-from-${i}`}><input id={`av-from-${i}`} type="time" className={c.input} value={r.start} onChange={(e) => patch(i, { start: e.target.value })} /></Field>
            <Field label={`To ${i + 1}`} htmlFor={`av-to-${i}`}><input id={`av-to-${i}`} type="time" className={c.input} value={r.end} onChange={(e) => patch(i, { end: e.target.value })} /></Field>
            <Button size="sm" variant="plain" aria-label={`Remove window ${i + 1}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</Button>
          </div>
        ))}
        <div><Button size="sm" onClick={() => setRows([...rows, { weekday: 1, start: "09:00", end: "17:00" }])}>Add a window</Button></div>
        <FormError message={error} />
      </div>
    </Drawer>
  );
}
